// Landing Page - Marketing and Value Proposition
import { ArrowRight, Sparkles, Folder, Zap, Cloud, Lock, Brain } from 'lucide-react';
import Button from '@/components/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/Card';
import { ThemeToggle } from '@/components/ThemeToggle';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold">FluxFeed</span>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Button onClick={() => window.location.href = '/app'}>
              Sign In
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-24 text-center">
        <div className="mx-auto max-w-3xl">
          <h1 className="mb-6 text-5xl font-bold tracking-tight">
            Your RSS Reader,{' '}
            <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
              Supercharged by AI
            </span>
          </h1>
          <p className="mb-8 text-xl text-muted-foreground">
            FluxFeed is a modern, cloud-native RSS reader built entirely on Cloudflare.
            Read smarter with AI-generated summaries, automatic tagging, and full-content rendering.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button size="lg" onClick={() => window.location.href = '/app'}>
              Get Started Free
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline">
              View on GitHub
            </Button>
          </div>
        </div>

        {/* App Preview */}
        <div className="mx-auto mt-16 max-w-5xl">
          <div className="rounded-lg border border-border bg-card shadow-2xl">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <div className="h-3 w-3 rounded-full bg-red-500"></div>
              <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
              <div className="h-3 w-3 rounded-full bg-green-500"></div>
            </div>
            <div className="aspect-video bg-muted p-8">
              <div className="grid h-full grid-cols-3 gap-4">
                <div className="rounded bg-background/50 p-4">
                  <div className="mb-3 h-4 w-3/4 rounded bg-primary/20"></div>
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-8 rounded bg-background/70"></div>
                    ))}
                  </div>
                </div>
                <div className="rounded bg-background/50 p-4">
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="rounded bg-background/70 p-3">
                        <div className="mb-2 h-3 w-2/3 rounded bg-muted"></div>
                        <div className="h-2 rounded bg-muted"></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded bg-background/50 p-4">
                  <div className="mb-3 h-4 rounded bg-primary/20"></div>
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-2 rounded bg-background/70"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="border-t border-border bg-muted/30 py-24">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold">Everything you need to read better</h2>
            <p className="text-lg text-muted-foreground">
              Powerful features built on Cloudflare's edge network
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <Brain className="mb-2 h-8 w-8 text-primary" />
                <CardTitle>AI-Powered Summaries</CardTitle>
                <CardDescription>
                  Get instant 2-3 sentence summaries of any article using Cloudflare Workers AI.
                  Never miss the key points.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <Sparkles className="mb-2 h-8 w-8 text-primary" />
                <CardTitle>Smart Tag Suggestions</CardTitle>
                <CardDescription>
                  AI automatically suggests relevant tags for your articles, keeping your library organized effortlessly.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <Cloud className="mb-2 h-8 w-8 text-primary" />
                <CardTitle>Full-Content Rendering</CardTitle>
                <CardDescription>
                  Read complete articles without leaving FluxFeed. Cloudflare Browser Rendering fetches clean markdown.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <Folder className="mb-2 h-8 w-8 text-primary" />
                <CardTitle>Folders & Organization</CardTitle>
                <CardDescription>
                  Organize feeds into nested folders with custom colors. Keep your reading list structured your way.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="mb-2 h-8 w-8 text-primary" />
                <CardTitle>Lightning Fast</CardTitle>
                <CardDescription>
                  Built entirely on Cloudflare Workers and D1. Blazing fast load times and automatic scaling worldwide.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <Lock className="mb-2 h-8 w-8 text-primary" />
                <CardTitle>Secure by Default</CardTitle>
                <CardDescription>
                  Authentication via Cloudflare Zero Trust. Your data is protected and private, always.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-24">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold">How it works</h2>
          <p className="text-lg text-muted-foreground">Get started in minutes</p>
        </div>

        <div className="mx-auto max-w-3xl space-y-8">
          <div className="flex gap-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              1
            </div>
            <div>
              <h3 className="mb-2 text-xl font-semibold">Sign in with Cloudflare Access</h3>
              <p className="text-muted-foreground">
                Secure authentication handled by Cloudflare Zero Trust. No passwords to manage.
              </p>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              2
            </div>
            <div>
              <h3 className="mb-2 text-xl font-semibold">Add your favorite RSS feeds</h3>
              <p className="text-muted-foreground">
                Import feeds from any source. Organize them into folders and customize your reading experience.
              </p>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              3
            </div>
            <div>
              <h3 className="mb-2 text-xl font-semibold">Read with AI assistance</h3>
              <p className="text-muted-foreground">
                Get AI summaries, automatic tags, and full-content rendering. Focus on what matters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-border bg-muted/30 py-24">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-3xl font-bold">Ready to upgrade your reading?</h2>
          <p className="mb-8 text-lg text-muted-foreground">
            Start using FluxFeed today. Free and open source.
          </p>
          <Button size="lg" onClick={() => window.location.href = '/app'}>
            Get Started Now
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Built with Cloudflare Workers, D1, KV, Workers AI, and Browser Rendering.</p>
          <p className="mt-2">Open source and powered by the edge.</p>
        </div>
      </footer>
    </div>
  );
}
